/**
 * Image compression utility for reducing file sizes before upload
 */

export class ImageCompressor {
    static async compressImage(file, options = {}) {
        const {
            maxWidth = 1920,
            maxHeight = 1080,
            quality = 0.8,
            maxSizeKB = 500
        } = options;

        return new Promise((resolve, reject) => {
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');
            const img = new Image();

            img.onload = () => {
                // Calculate new dimensions
                let { width, height } = img;
                
                if (width > maxWidth || height > maxHeight) {
                    const ratio = Math.min(maxWidth / width, maxHeight / height);
                    width *= ratio;
                    height *= ratio;
                }

                canvas.width = width;
                canvas.height = height;

                // Draw and compress
                ctx.drawImage(img, 0, 0, width, height);

                // Try different quality levels if file is still too large
                let currentQuality = quality;
                const attemptCompression = () => {
                    canvas.toBlob((blob) => {
                        if (!blob) {
                            reject(new Error('Failed to compress image'));
                            return;
                        }

                        const sizeKB = blob.size / 1024;
                        
                        if (sizeKB <= maxSizeKB || currentQuality <= 0.1) {
                            // Create a new file with the compressed blob
                            const compressedFile = new File([blob], file.name, {
                                type: blob.type,
                                lastModified: Date.now()
                            });
                            resolve(compressedFile);
                        } else {
                            // Reduce quality and try again
                            currentQuality -= 0.1;
                            attemptCompression();
                        }
                    }, file.type, currentQuality);
                };

                attemptCompression();
            };

            img.onerror = () => reject(new Error('Failed to load image'));
            img.src = URL.createObjectURL(file);
        });
    }

    static getFileTypeCategory(file) {
        const imageTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
        const pdfTypes = ['application/pdf'];
        const documentTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];

        if (imageTypes.includes(file.type)) return 'images';
        if (pdfTypes.includes(file.type)) return 'pdf';
        if (documentTypes.includes(file.type)) return 'documents';
        return 'other';
    }

    static validateFileType(file, allowedTypes) {
        if (allowedTypes === 'all') return true;
        
        const category = this.getFileTypeCategory(file);
        return allowedTypes === category;
    }

    static formatFileSize(bytes) {
        if (!bytes) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    }
}
